import { useState, useEffect, useCallback } from 'react';
import { UserService } from '@/services';
import type { User, CompanyProfile, BranchOffice } from '@/services/userService';
import { toast } from 'sonner';
import { useSupabase } from './useSupabase';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('useProfiles', 'hooks');

// Hook for fetching users
export function useUsers() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const supabase = useSupabase();

  const fetchProfiles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await UserService.getAllLoanOfficers(supabase);
      setUsers(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load loan officers';
      setError(errorMessage);
      console.error('Error fetching loan officers:', err);
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  return { users, loading, error, refetch: fetchProfiles };
}

// Hook for current user's loan officer profile
export function useCurrentUser() {
  const [profile, setProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const supabase = useSupabase();

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error } = await UserService.getCurrentUser(supabase);
      if (error) {
        throw new Error(error.message || 'Failed to load profile');
      }
      setProfile(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load profile';
      setError(errorMessage);
      console.error('Error fetching current user profile:', err);
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  return { profile, loading, error, refetch: fetchProfile };
}

// Hook for profile mutations
export function useUserMutations() {
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const supabase = useSupabase();

  const updateProfile = useCallback(async (
    userId: string,
    updates: Partial<User>
  ): Promise<User | null> => {
    log.info('updateProfile called', {
      userId,
      updateKeys: Object.keys(updates),
      hasSupabase: !!supabase,
      websiteTemplate: updates.websiteTemplate
    });

    try {
      setUpdating(true);
      setError(null);

      // Check if supabase client has session
      const { data: { session } } = await supabase.auth.getSession();
      log.info('Session check before update', {
        hasSession: !!session,
        userId: session?.user?.id,
        targetUserId: userId
      });

      const { data: updatedProfile, error } = await UserService.updateUser(userId, updates, supabase);

      log.info('UserService.updateUser result', {
        success: !!updatedProfile,
        hasError: !!error,
        errorMessage: error?.message || error
      });

      if (error) {
        throw new Error(error.message || 'Failed to update profile');
      }
      toast.success('Profile updated successfully');
      return updatedProfile;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update profile';
      log.error('updateProfile failed', { userId, errorMessage }, err as Error);
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setUpdating(false);
    }
  }, [supabase]);

  const createProfile = useCallback(async (
    profile: Partial<User>
  ): Promise<User | null> => {
    try {
      setUpdating(true);
      setError(null);
      const { data: newProfile, error } = await UserService.createUser(profile, supabase);
      if (error) {
        throw new Error(error.message || 'Failed to create profile');
      }
      toast.success('Profile created successfully');
      return newProfile;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create profile';
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setUpdating(false);
    }
  }, [supabase]);

  return {
    updateProfile,
    createProfile,
    updating,
    error
  };
}

// Hook for company profile
export function useCompanyProfile() {
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await UserService.getCurrentCompanyProfile();
      setProfile(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load company profile';
      setError(errorMessage);
      console.error('Error fetching company profile:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  return { profile, loading, error, refetch: fetchProfile };
}

// Hook for company profile mutations
export function useCompanyProfileMutations() {
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateProfile = useCallback(async (
    updates: Partial<CompanyProfile>
  ): Promise<CompanyProfile | null> => {
    try {
      setUpdating(true);
      setError(null);
      const updatedProfile = await UserService.updateCompanyProfile(updates);
      if (!updatedProfile) {
        throw new Error('Could not verify the company profile save. Please try again.');
      }
      toast.success('Company profile updated successfully');
      return updatedProfile;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update company profile';
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setUpdating(false);
    }
  }, []);

  const createProfile = useCallback(async (
    companyData: Partial<CompanyProfile>
  ): Promise<CompanyProfile | null> => {
    try {
      setUpdating(true);
      setError(null);
      const newProfile = await UserService.createCompanyProfile(companyData);
      toast.success('Company profile created successfully');
      return newProfile;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create company profile';
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setUpdating(false);
    }
  }, []);

  return {
    updateProfile,
    createProfile,
    updating,
    error
  };
}

// Hook for branch offices
export function useBranchOffices() {
  const [branches, setBranches] = useState<BranchOffice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const supabase = useSupabase();

  const fetchBranches = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await UserService.getAllBranchOffices(supabase);
      setBranches(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load branch offices';
      setError(errorMessage);
      console.error('Error fetching branch offices:', err);
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  return { branches, loading, error, refetch: fetchBranches };
}

// Hook for branch office mutations
export function useBranchOfficeMutations() {
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createBranch = useCallback(async (
    branch: Omit<BranchOffice, 'id' | 'created_at' | 'updated_at'>
  ): Promise<BranchOffice | null> => {
    try {
      setUpdating(true);
      setError(null);
      const newBranch = await UserService.createBranchOffice(branch);
      toast.success('Branch office created successfully');
      return newBranch;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create branch office';
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setUpdating(false);
    }
  }, []);

  const updateBranch = useCallback(async (
    id: string,
    updates: Partial<BranchOffice>
  ): Promise<BranchOffice | null> => {
    try {
      setUpdating(true);
      setError(null);
      const updatedBranch = await UserService.updateBranchOffice(id, updates);
      toast.success('Branch office updated successfully');
      return updatedBranch;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update branch office';
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setUpdating(false);
    }
  }, []);

  const deleteBranch = useCallback(async (id: string): Promise<boolean> => {
    try {
      setUpdating(true);
      setError(null);
      await UserService.deleteBranchOffice(id);
      toast.success('Branch office deleted successfully');
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete branch office';
      setError(errorMessage);
      toast.error(errorMessage);
      return false;
    } finally {
      setUpdating(false);
    }
  }, []);

  return {
    createBranch,
    updateBranch,
    deleteBranch,
    updating,
    error
  };
}
